import { canSwallowError } from "@sunrise/error";
import { hostsAtom, httpClientAtom } from "@sunrise/http-client";
import { selectIsLoggedIn, selectJwtUserToken } from "@sunrise/jwt";
import { type Nullable, isNil } from "@sunrise/utils";
import { atomWithInfiniteQuery } from "jotai-tanstack-query";
import { atomFamily } from "jotai/utils";

import { RECORDING_DATA_STALE_TIME_IN_MS } from "../constants";
import { recordingsSortForSeriesAssetIdAtom } from "../recordings-sort-for-series-asset-id.atom";
import { recordingsStaleTimeAtom } from "../recordings-stale-time.atom";
import { fetchRecordingGroupItems } from "../recordings.service";
import type { AssetId, RecordingGroupId } from "@sunrise/backend-types-core";
import {
  queryKeys,
  RecordingsQueryInfiniteData,
  RecordingsResponse,
} from "@sunrise/backend-types";

type RecordingGroupItemsQueryAtomArgs = {
  recordingGroupId: RecordingGroupId;
  seriesAssetId: AssetId;
  traverseChildGroups: boolean;
};

/**
 * This is an atom that will ask the backend for all episodes recordings of a specific recordingGroupId (series).
 * It will cache the results through react-query.
 */
export const recordingGroupItemsQueryAtom = atomFamily(
  ({
    recordingGroupId,
    seriesAssetId,
    traverseChildGroups,
  }: RecordingGroupItemsQueryAtomArgs) => {
    const innerAtom = atomWithInfiniteQuery<
      Nullable<RecordingsResponse["data"]>,
      unknown,
      RecordingsQueryInfiniteData,
      ReturnType<typeof queryKeys.recordingGroupItems>,
      number
    >((get) => {
      const host = get(hostsAtom).api;
      if (isNil(host)) throw new Error("Host is not set");
      const { privateApi } = get(httpClientAtom);
      if (!privateApi) throw new Error("missing privateApi");

      const isLoggedIn = get(selectIsLoggedIn);

      const sort = get(recordingsSortForSeriesAssetIdAtom({ seriesAssetId }));
      const queryKey = queryKeys.recordingGroupItems(
        get(selectJwtUserToken),
        recordingGroupId,
        sort,
        traverseChildGroups,
      );

      return {
        initialPageParam: 1,
        staleTime: get(recordingsStaleTimeAtom),
        // We want the GC time to be not too high here since this is remembered per series. It needs to be garbage collected pretty quickly.
        gcTime: RECORDING_DATA_STALE_TIME_IN_MS,
        getNextPageParam: (lastPage) => {
          if (isNil(lastPage)) return undefined;

          const { page, pages } = lastPage;
          return page < pages ? page + 1 : undefined;
        },
        // NOTE: We still depend on the access token since we want the data to reload when the user's token changes.
        queryKey,
        queryFn: async ({ pageParam }) => {
          try {
            if (!isLoggedIn) {
              return null;
            }

            return fetchRecordingGroupItems(
              host,
              privateApi,
              recordingGroupId,
              traverseChildGroups,
              sort,
              pageParam,
            );
          } catch (e) {
            if (!canSwallowError(e, true)) throw e;
            console.error(e);
            return null;
          }
        },
        keepPreviousData: true,
        suspense: true,
      };
    });
    innerAtom.debugLabel = `recordingGroupItemsQueryAtom(${recordingGroupId})`;
    return innerAtom;
  },
);
